import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  FlipHorizontal,
  History,
  ListChecks,
  SkipBack,
  SkipForward,
} from 'lucide-react';
import { useApi } from '../api/api-context';
import { useJob } from '../api/useJob';
import type { GameRepertoireMatch, GameSummary, Job, UciMove } from '../api/types';
import { replayLine, sanLabel } from '../board/notation';
import { momentArrows, type PreparedMove } from '../board/review-arrows';
import { useLocalAnalysisPreference, writeLocalAnalysisPreference } from '../engine/preference';
import { useEngineSettings } from '../engine/settings';
import { isLocalAnalysisActive, useLocalAnalysis } from '../engine/useLocalAnalysis';
import {
  MOMENT_BADGES,
  bookPlies,
  bookSummary,
  explainMoment,
  isRelevantMatch,
  keyMoments,
  moveText,
  preparedMoves,
} from '../state/game-review';
import { reviewRoute } from '../state/review-session';
import { buildNodeRoute } from '../state/route';
import { BoardView } from './BoardView';
import { EngineStrip, EvalBar } from './LocalEval';
import { GameMoveList } from './GameMoveList';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const STARTING_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

const SOURCE_LABELS: Record<GameSummary['source'], string> = {
  chesscom: 'Chess.com', lichess: 'Lichess', pgn: 'PGN', manual: 'Manual',
};

type RunState =
  | { phase: 'idle' | 'comparing' }
  | { phase: 'failed'; message: string }
  | { phase: 'ready'; events: NonNullable<ReturnType<typeof useRunEvents>['events']> };

function useRunEvents(runId: string | null) {
  const api = useApi();
  const events = useInfiniteQuery({
    queryKey: ['deviation-events', runId],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => api.listDeviationEvents(runId!, 200, pageParam),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: runId !== null,
  });
  // Moments need the whole game, so keep paging until the last event.
  useEffect(() => {
    if (events.hasNextPage && !events.isFetchingNextPage) void events.fetchNextPage();
  }, [events.hasNextPage, events.isFetchingNextPage]); // eslint-disable-line react-hooks/exhaustive-deps
  const complete = events.isSuccess && !events.hasNextPage;
  return {
    events: complete ? events.data.pages.flatMap((page) => page.items) : null,
    isError: events.isError,
    retry: () => void events.refetch(),
  };
}

/** Start (idempotently) and follow the server comparison of one game with one repertoire. */
function useDeviationRun(gameId: string, repertoireId: string | null): RunState & { retry: () => void } {
  const api = useApi();
  const [accepted, setAccepted] = useState<Job | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const { job, ui } = useJob(accepted?.jobId ?? null, accepted);
  const runId = ui.succeeded && typeof job?.result?.runId === 'string' ? job.result.runId : null;
  const run = useQuery({
    queryKey: ['deviation-run', runId],
    queryFn: () => api.getDeviationRun(runId!),
    enabled: runId !== null,
  });
  const events = useRunEvents(runId);
  const started = useRef<string | null>(null);

  useEffect(() => {
    const key = `${gameId}:${repertoireId}:${attempt}`;
    if (repertoireId === null || started.current === key) return;
    started.current = key;
    setAccepted(null);
    setError(null);
    // Runs are idempotent per game, repertoire and detector config: repeats are cheap.
    api.createDeviationRun(gameId, { repertoireId, includeEngine: false, suboptimalThresholdCp: 80 }, crypto.randomUUID())
      .then(setAccepted)
      .catch((cause: unknown) => setError(cause instanceof Error ? cause.message : 'Could not start the comparison.'));
  }, [api, gameId, repertoireId, attempt]);

  const retry = () => setAttempt((value) => value + 1);
  if (repertoireId === null) return { phase: 'idle', retry };
  if (error) return { phase: 'failed', message: error, retry };
  if (accepted && (ui.failed || ui.status === 'cancelled')) return { phase: 'failed', message: 'The comparison did not finish.', retry };
  if (accepted && ui.succeeded && !runId) return { phase: 'failed', message: 'The comparison finished without a result.', retry };
  if (run.isError || events.isError) return { phase: 'failed', message: 'Could not load the comparison.', retry };
  if (run.data?.status === 'succeeded' && events.events) return { phase: 'ready', events: events.events, retry };
  return { phase: 'comparing', retry };
}

export interface GameReviewProps {
  gameId: string;
  repertoireId: string | null;
  match: GameRepertoireMatch | undefined;
  games: readonly GameSummary[];
  matches: ReadonlyMap<string, GameRepertoireMatch>;
  onSelectGame: (gameId: string) => void;
  onSelectRepertoire: (repertoireId: string) => void;
}

/** Game review in the builder's layout: board + engine on the left, review rail on the right. */
export function GameReview({
  gameId,
  repertoireId,
  match,
  games,
  matches,
  onSelectGame,
  onSelectRepertoire,
}: GameReviewProps): JSX.Element {
  const api = useApi();
  const game = useQuery({ queryKey: ['game', gameId], queryFn: () => api.getGame(gameId) });
  const run = useDeviationRun(gameId, repertoireId);
  const startFen = game.data ? game.data.initialFen ?? STARTING_FEN : null;
  const replay = useMemo(
    () => (startFen && game.data ? replayLine(startFen, game.data.movesUci) : null),
    [startFen, game.data],
  );
  const events = run.phase === 'ready' ? run.events : null;
  const moments = useMemo(() => (events ? keyMoments(events) : []), [events]);
  const plyCount = replay?.sans.length ?? 0;
  const [frame, setFrame] = useState<number | null>(null);
  const [tab, setTab] = useState('review');
  const [flipped, setFlipped] = useState(false);
  // Open on the first moment once it is known; a user's own navigation always wins.
  const current = Math.min(frame ?? moments[0]?.ply ?? 0, plyCount);
  const moment = moments.find((m) => m.ply === current && m.ply < plyCount) ?? null;
  const momentIndex = moment ? moments.indexOf(moment) : -1;

  const node = useQuery({
    queryKey: ['deviation-node', repertoireId, moment?.event.source.zobristHash, moment?.event.source.positionId],
    queryFn: () => api.getNode(repertoireId!, moment!.event.source.zobristHash, moment!.event.source.positionId),
    enabled: moment !== null && repertoireId !== null,
    retry: false,
  });

  const fen = replay?.fens[current] ?? startFen ?? STARTING_FEN;
  const engineEnabled = useLocalAnalysisPreference();
  const engineSettings = useEngineSettings();
  const analysis = useLocalAnalysis(replay ? fen : null, engineEnabled, {
    depth: engineSettings.depth,
    multiPv: engineSettings.lines,
  });

  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (target && ['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName)) return;
      if (target?.closest('[role="application"]')) return; // the board uses arrows for its cursor
      if (event.key === 'ArrowLeft') setFrame(Math.max(0, current - 1));
      else if (event.key === 'ArrowRight') setFrame(Math.min(plyCount, current + 1));
      else if (event.key.toLowerCase() === 'f' && !event.ctrlKey && !event.metaKey) setFlipped((value) => !value);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [current, plyCount]);

  if (game.isError) return <main className="page"><p role="alert">Could not load this game.</p></main>;
  if (!game.data || !replay || !startFen) return <main className="page"><p role="status">Loading the game…</p></main>;

  const data = game.data;
  const userColor = data.userColor ?? null;
  const baseOrientation = userColor ?? 'w';
  const orientation = flipped ? (baseOrientation === 'w' ? 'b' : 'w') : baseOrientation;
  const turn = fen.split(/\s+/)[1] === 'b' ? 'b' : 'w';
  // Without a finished comparison nothing is known to be book.
  const inBook = run.phase === 'ready' ? bookPlies(moments, plyCount) : 0;
  const repertoire = match?.matches.find((m) => m.repertoireId === repertoireId) ?? null;
  const switchable = (match?.matches ?? []).filter((m) => isRelevantMatch(m) || m.repertoireId === repertoireId);
  const nodeMatches = moment !== null && node.data !== undefined &&
    node.data.data.position.zobristHash === moment.event.source.zobristHash &&
    node.data.data.position.positionId === moment.event.source.positionId;
  const prepared: Array<PreparedMove & { san: string }> = moment === null
    ? []
    : nodeMatches
      ? preparedMoves(node.data!.data.outgoing)
      : moment.event.expectedMoves.map((uci) => ({
        uci: uci as UciMove,
        san: sanLabel(fen, uci),
        role: moment.kind === 'opponent_novelty' || moment.kind === 'coverage_gap' ? 'opponent' : 'main',
      }));
  const leftAt = moment
    ? [...moments].reverse().find((m) => m.ply < moment.ply && (m.kind === 'novelty' || m.kind === 'opponent_novelty'))
    : undefined;
  const explanation = moment ? explainMoment(moment, { startFen, sans: replay.sans, userColor, prepared, leftAt }) : null;
  const arrows = moment
    ? momentArrows(moment.event.actualMove as UciMove, prepared, {
      transposition: moment.kind === 'transposition_entry' || moment.kind === 'transposition_reentry',
    })
    : [];
  const nextMoment = moments.find((m) => m.ply > current || (m.ply === current && moment === null));
  const previousMoment = [...moments].reverse().find((m) => m.ply < current);
  const builderTarget = moment === null || repertoireId === null
    ? null
    : moment.kind === 'transposition_reentry' ? moment.event.target ?? null : moment.event.source;
  const builderLink = builderTarget && repertoireId
    ? buildNodeRoute({ repertoireId, zobristHash: builderTarget.zobristHash, positionId: builderTarget.positionId, path: null })
    : null;
  const lastSan = current > 0 ? replay.sans[current - 1] : undefined;
  const lastMoveText = lastSan ? moveText(current - 1, startFen, lastSan) : null;
  const colorName = userColor === 'w' ? 'White' : userColor === 'b' ? 'Black' : null;

  return (
    <main className="analysis-layout focused-builder game-review-layout">
      <section className="analysis-center" aria-label="Board">
        <header className="studio-context">
          <p className="studio-eyebrow">GAME REVIEW</p>
          <h1>{data.white} vs {data.black}</h1>
          <p className="game-review-meta">
            {data.result}
            {data.playedAt && ` · ${new Date(data.playedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`}
            {` · ${SOURCE_LABELS[data.source]}`}
            {colorName && ` · you played ${colorName}`}
          </p>
          <div className="game-review-against">
            {repertoireId === null ? (
              <span className="muted">No matching repertoire</span>
            ) : switchable.length > 1 ? (
              <label>
                Compared with{' '}
                <select value={repertoireId} onChange={(event) => onSelectRepertoire(event.target.value)}>
                  {switchable.map((m) => <option key={m.repertoireId} value={m.repertoireId}>{m.name}</option>)}
                </select>
              </label>
            ) : (
              <span>Compared with <Link to={`/repertoires/${repertoireId}`}>{repertoire?.name ?? 'your repertoire'}</Link></span>
            )}
          </div>
          <p className="status" data-testid="status-text">
            {run.phase === 'ready' ? bookSummary(moments, replay.sans, startFen, userColor) : run.phase === 'comparing' ? 'Comparing with your repertoire…' : ' '}
          </p>
        </header>
        <div className="board-with-eval">
          {isLocalAnalysisActive(analysis) && <EvalBar analysis={analysis} sideToMove={turn} orientation={orientation} />}
          <BoardView
            input={{
              fen,
              turnColor: turn,
              orientation,
              dests: [],
              lastMove: current > 0 ? (data.movesUci[current - 1] as UciMove) : undefined,
              arrows,
            }}
            onMove={() => false}
            label={`Game review chessboard, ${turn === 'w' ? 'white' : 'black'} to move`}
          />
        </div>
        <nav className="board-controls flex items-center justify-between" aria-label="Game navigation">
          <button type="button" aria-label="Flip board" title="Flip board (F)" onClick={() => setFlipped((value) => !value)}>
            <FlipHorizontal size={18} />
          </button>
          <div className="board-navigation">
            <button type="button" aria-label="First move" disabled={current === 0} onClick={() => setFrame(0)}><SkipBack size={18} /></button>
            <button type="button" aria-label="Previous move" disabled={current === 0} onClick={() => setFrame(current - 1)}><ChevronLeft size={20} /></button>
            <button type="button" aria-label="Next move" disabled={current >= plyCount} onClick={() => setFrame(current + 1)}><ChevronRight size={20} /></button>
            <button type="button" aria-label="Last move" disabled={current >= plyCount} onClick={() => setFrame(plyCount)}><SkipForward size={18} /></button>
          </div>
          <span className="game-review-position" aria-live="polite">{lastMoveText ?? 'Start'}</span>
        </nav>
        <EngineStrip analysis={analysis} fen={fen} sideToMove={turn} enabled={engineEnabled} onToggle={writeLocalAnalysisPreference} />
      </section>

      <aside className="analysis-right builder-rail" aria-label="Game review">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList aria-label="Game review tools">
            <TabsTrigger value="review"><ListChecks size={16} />Review</TabsTrigger>
            <TabsTrigger value="games"><History size={16} />Games</TabsTrigger>
          </TabsList>
          <TabsContent value="review">
            <section className="move-suggestions review-moment" aria-labelledby="moment-title" aria-live="polite">
              {repertoireId === null ? (
                <>
                  <p className="studio-eyebrow">NO MATCH</p>
                  <h2 id="moment-title">None of your repertoires covers this game</h2>
                  <p className="muted">
                    {colorName
                      ? `You played ${colorName}, and none of your ${colorName} repertoires reaches this opening.`
                      : 'None of your repertoires reaches this opening.'}
                  </p>
                  {(match?.matches.length ?? 0) > 0 && (
                    <ul className="suggestion-list">
                      {match!.matches.map((m) => (
                        <li key={m.repertoireId}>
                          <button type="button" onClick={() => onSelectRepertoire(m.repertoireId)}>
                            <strong>{m.name}</strong>
                            <span className="suggestion-stats"><span>Compare anyway</span></span>
                            <span className="suggestion-action">Compare</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ) : run.phase === 'comparing' || run.phase === 'idle' ? (
                <>
                  <p className="studio-eyebrow">COMPARING</p>
                  <h2 id="moment-title">Checking the game against {repertoire?.name ?? 'your repertoire'}</h2>
                  <p className="muted" role="status">Finding where the game left your preparation…</p>
                </>
              ) : run.phase === 'failed' ? (
                <>
                  <p className="studio-eyebrow">COMPARISON</p>
                  <h2 id="moment-title">The comparison did not finish</h2>
                  <p role="alert">{run.message} <button type="button" onClick={run.retry}>Try again</button></p>
                </>
              ) : moment && explanation ? (
                <>
                  <p className="studio-eyebrow">MOMENT {momentIndex + 1} OF {moments.length} · {MOMENT_BADGES[moment.kind].toUpperCase()}</p>
                  <h2 id="moment-title">{explanation.title}</h2>
                  <p className="moment-body">{explanation.body}</p>
                  {moment.event.explanation && <p className="muted">{moment.event.explanation}</p>}
                  <ul className="suggestion-list moment-moves" aria-label="Played and prepared moves">
                    {arrows.map((arrow) => {
                      const played = arrow.uci === moment.event.actualMove;
                      const prep = prepared.find((p) => p.uci === arrow.uci);
                      const san = played ? replay.sans[moment.ply] : prep?.san ?? arrow.uci;
                      const role = prep?.role === 'alternative' ? 'Alternative' : prep?.role === 'opponent' ? 'Prepared reply' : prep ? 'Main line' : null;
                      return (
                        <li key={arrow.uci} className={`arrow-row arrow-swatch-${arrow.brush}`}>
                          <div className="moment-move">
                            <strong>{moveText(moment.ply, startFen, san)}</strong>
                            <span className="suggestion-stats">
                              <span>{played ? (role ? `Played · ${role}` : 'Played') : role}</span>
                            </span>
                            <span className="suggestion-action">{played ? (prep || moment.kind.startsWith('transposition') ? 'In repertoire' : 'Not prepared') : 'Your prep'}</span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="review-actions">
                    {nextMoment && <button type="button" className="studio-primary" onClick={() => setFrame(nextMoment.ply)}>Next moment</button>}
                    {builderLink && <Link className="button-link" to={builderLink}>{explanation.extend ? 'Add a move here' : 'Open in builder'}</Link>}
                    {repertoireId && moment.kind !== 'opponent_novelty' && moment.kind !== 'coverage_gap' &&
                      moment.kind !== 'transposition_reentry' && prepared.some((p) => p.role !== 'opponent') && (
                      <Link className="button-link" to={reviewRoute(repertoireId, { mode: 'all', scope: { zobristHash: moment.event.source.zobristHash, positionId: moment.event.source.positionId } })}>
                        Drill this position
                      </Link>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <p className="studio-eyebrow">{lastMoveText ? `AFTER ${lastMoveText.toUpperCase()}` : 'START'}</p>
                  <h2 id="moment-title">
                    {moments.length === 0
                      ? 'The game stayed in your preparation'
                      : current === 0 ? 'Start of the game' : current <= inBook ? 'Still in your preparation' : 'Outside your preparation'}
                  </h2>
                  <p className="muted">
                    {moments.length === 0
                      ? 'No departures from your preparation in this game.'
                      : 'Step through the game, or jump to the moments where it met your repertoire.'}
                  </p>
                  <div className="review-actions">
                    {previousMoment && <button type="button" className="secondary" onClick={() => setFrame(previousMoment.ply)}>Previous moment</button>}
                    {nextMoment && <button type="button" className="studio-primary" onClick={() => setFrame(nextMoment.ply)}>Next moment</button>}
                  </div>
                </>
              )}
            </section>

            {moments.length > 0 && (
              <section className="line-browser" aria-labelledby="moments-title">
                <h2 id="moments-title">Moments</h2>
                <ul>
                  {moments.map((m) => (
                    <li key={m.event.eventId} className="builder-line-row">
                      <button
                        type="button"
                        className={`builder-line-open${m.ply === current ? ' current' : ''}`}
                        aria-current={m.ply === current ? 'true' : undefined}
                        onClick={() => setFrame(m.ply)}
                      >
                        <span>{MOMENT_BADGES[m.kind]}</span>
                        <small>{moveText(m.ply, startFen, replay.sans[m.ply] ?? m.event.actualMove)}</small>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <GameMoveList
              sans={replay.sans}
              startFen={startFen}
              moments={moments}
              inBook={inBook}
              activePly={moment ? moment.ply : current > 0 ? current - 1 : null}
              onSelect={(ply) => setFrame(moments.some((m) => m.ply === ply) ? ply : ply + 1)}
            />
            <p className="context-hint">← → step through the game · F flips the board.</p>
          </TabsContent>
          <TabsContent value="games">
            <GameList games={games} matches={matches} currentGameId={gameId} onSelect={(id) => { setTab('review'); onSelectGame(id); }} />
          </TabsContent>
        </Tabs>
      </aside>
    </main>
  );
}

export function GameList({
  games,
  matches,
  currentGameId,
  onSelect,
}: {
  games: readonly GameSummary[];
  matches: ReadonlyMap<string, GameRepertoireMatch>;
  currentGameId: string | null;
  onSelect: (gameId: string) => void;
}): JSX.Element {
  return (
    <section className="line-browser" aria-labelledby="recent-games-title">
      <h2 id="recent-games-title">Recent games</h2>
      <ul>
        {games.map((summary) => {
          const best = matches.get(summary.gameId)?.matches.find(isRelevantMatch);
          const selected = summary.gameId === currentGameId;
          return (
            <li key={summary.gameId} className="builder-line-row">
              <button
                type="button"
                className={`builder-line-open${selected ? ' current' : ''}`}
                aria-current={selected ? 'true' : undefined}
                onClick={() => onSelect(summary.gameId)}
              >
                <span>
                  {summary.white} vs {summary.black} · {summary.result}
                  {summary.playedAt && ` · ${new Date(summary.playedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`}
                </span>
                <small>
                  {best
                    ? `${best.name} · ${best.bookPlies === 0
                      ? 'transposes into your prep'
                      : best.bookPlies >= summary.plyCount
                        ? 'stayed in your prep'
                        : `left prep on move ${Math.floor(best.bookPlies / 2) + 1}`}`
                    : 'No matching repertoire'}
                </small>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
